import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface CertificationsProps {
  t: any;
  lang: Language;
}

const CERT_I18N: Record<Language, {
  header_badge: string;
  header_title: string;
  header_subtitle: string;
  sust_badge: string;
  sust_title: string;
  sust_desc: string;
  sust_verify: string;
  sello_title: string;
  sello_desc: string;
  zero_badge: string;
  zero_title: string;
  zero_desc: string;
  regen_badge: string;
  regen_title: string;
  regen_desc: string;
  legal_badge: string;
  legal_title: string;
  legal_desc: string;
  legal_item1_label: string;
  legal_item1_text: string;
  legal_item2_label: string;
  legal_item2_text: string;
  legal_btn: string;
  legal_qr_badge: string;
  legal_qr_text: string;
  tech_badge: string;
  tech_title: string;
  tech_angm_label: string;
  tech_angm_desc: string;
  tech_wfr_label: string;
  tech_wfr_desc: string;
  tech_gps_label: string;
  tech_gps_desc: string;
  rescue_badge: string;
  rescue_desc: string;
  rescue_item1: string;
  rescue_item2: string;
  rescue_item3: string;
  rescue_btn: string;
  rescue_note: string;
  plan_title: string;
  plan_desc: string;
  plan_btn: string;
  plan_wa: string;
}> = {
  es: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Compromiso Sustentable',
    header_subtitle: 'Nuestras políticas de sustentabilidad con SERNATUR, la ruta hacia la emisión cero y las certificaciones oficiales de montaña en Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CHILE',
    sust_title: 'Turismo Sustentable',
    sust_desc: 'Firmado oficialmente bajo las directrices de buenas prácticas de Sernatur.',
    sust_verify: 'Verificar en Sernatur',
    sello_title: 'Nuestro camino hacia el Sello S',
    sello_desc: 'En Fisura Expeditions buscamos que la sustentabilidad sea nuestro principal diferenciador. Firmamos formalmente el Compromiso de Turismo Sustentable con SERNATUR y nos encontramos en proceso de conseguir el Sello S, la distinción de sustentabilidad más rigurosa de Chile, con la meta de obtenerla en el transcurso del año.',
    zero_badge: 'Mediano Plazo · Emisión Cero',
    zero_title: 'Tours con emisión cero de carbono',
    zero_desc: 'Avanzamos hacia el transporte de expedición en vans eléctricas, contratación exclusiva de guías locales y arrieros de Villa Cerro Castillo, y alimentación en empaques biodegradables con reciclaje total. Al finalizar cada tour, entregamos a nuestros clientes una ficha de sustentabilidad que detalla el CO₂ evitado y los kilos reciclados.',
    regen_badge: 'Largo Plazo · Turismo Regenerativo',
    regen_title: 'Conservación y regeneración activa',
    regen_desc: 'Sumamos a nuestras expediciones acciones directas junto a los viajeros, como la plantación de especies nativas en la cuenca del Río Ibáñez y jornadas de limpieza y reciclaje en los senderos del Parque Nacional Cerro Castillo.',
    legal_badge: 'MARCO LEGAL · LEY N° 20.423',
    legal_title: 'Registro Nacional SERNATUR',
    legal_desc: 'Fisura Expeditions figura formalmente inscrita en el Registro Nacional de Prestadores de Servicios Turísticos de SERNATUR, cumpliendo con las exigencias del Estado de Chile para turismo aventura y guiado de montaña.',
    legal_item1_label: 'Plan de Gestión de Riesgos:',
    legal_item1_text: 'Protocolos de emergencia, contingencia climática y evacuación médica ingresados ante la autoridad.',
    legal_item2_label: 'Equipamiento homologado:',
    legal_item2_text: 'Cuerdas, arneses y material técnico con certificación internacional UIAA / CE y control de vida útil.',
    legal_btn: 'Ver Ficha Oficial SERNATUR',
    legal_qr_badge: 'FICHA SERVICIO Nº 90240',
    legal_qr_text: 'Escanea para consultar registro',
    tech_badge: 'ESTÁNDARES TÉCNICOS',
    tech_title: 'Guías y Seguridad en Terreno',
    tech_angm_label: 'Guías de Montaña',
    tech_angm_desc: 'Asociación Nacional de Guías de Montaña. Competencias técnicas en roca, glaciares y rescate alpino.',
    tech_wfr_label: 'Medicina Agreste',
    tech_wfr_desc: 'Wilderness First Responder. Primeros auxilios avanzados y estabilización de pacientes en zonas remotas.',
    tech_gps_label: 'Comunicación Satelital',
    tech_gps_desc: 'Dispositivo satelital bidireccional Iridium en cada grupo con rastreo de posición y botón SOS de emergencia.',
    rescue_badge: 'ASEGURA TU EXPEDICIÓN',
    rescue_desc: 'Las expediciones en la Patagonia transitan por terrenos agrestes y remotos. A través de nuestra alianza con Global Rescue, puedes proteger tu viaje con cobertura de rescate de campo, evacuación aeromédica y asesoría médica de urgencia 24/7 en cualquier punto de la ruta.',
    rescue_item1: 'Rescate de campo y evacuación aeromédica',
    rescue_item2: 'Asesoría médica agreste 24/7',
    rescue_item3: 'Coordinación directa con nuestro equipo en terreno',
    rescue_btn: 'Asegurar en Global Rescue',
    rescue_note: 'Partner Oficial · Cobertura sin deducible',
    plan_title: 'Planifica tu expedición en Aysén',
    plan_desc: 'Revisa nuestras rutas en Villa Cerro Castillo o escríbenos directamente por WhatsApp para coordinar tu salida.',
    plan_btn: 'Ver Expediciones',
    plan_wa: 'Consultar por WhatsApp'
  },
  en: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Sustainability & Accreditations',
    header_subtitle: 'Our environmental commitment with SERNATUR, roadmap toward zero emissions, and official mountain certifications in Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CHILE',
    sust_title: 'Sustainable Tourism',
    sust_desc: "Officially signed under Sernatur's good sustainability practices guidelines.",
    sust_verify: 'Verify on Sernatur',
    sello_title: 'Our Journey to the S Seal',
    sello_desc: "At Fisura Expeditions, sustainability is our core commitment. We officially signed the Sustainable Tourism Commitment with SERNATUR and are currently working to achieve the S Seal, Chile's most demanding sustainability distinction, aiming to receive it during this year.",
    zero_badge: 'Medium Term · Zero Emissions',
    zero_title: 'Zero carbon emission tours',
    zero_desc: 'We are transitioning to electric expedition vans, exclusively hiring local mountain guides and packhorse wranglers from Villa Cerro Castillo, and providing food in biodegradable, fully recyclable packaging. At the end of every trip, we provide travelers with a sustainability report detailing avoided CO₂ and recycled materials.',
    regen_badge: 'Long Term · Regenerative Tourism',
    regen_title: 'Active conservation & regeneration',
    regen_desc: 'We integrate hands-on conservation actions directly with travelers, such as planting native tree species along the Ibáñez River basin and organizing cleanup and recycling days along Cerro Castillo National Park trails.',
    legal_badge: 'LEGAL FRAMEWORK · LAW Nº 20,423',
    legal_title: 'National SERNATUR Registry',
    legal_desc: "Fisura Expeditions is officially registered in Chile's National Registry of Tourism Service Providers (SERNATUR), complying with all government safety standards for adventure tourism and mountain guiding.",
    legal_item1_label: 'Risk Management Plan:',
    legal_item1_text: 'Emergency protocols, weather contingency, and medical evacuation plans formally registered with authorities.',
    legal_item2_label: 'Certified Equipment:',
    legal_item2_text: 'Ropes, harnesses, and technical mountain gear with UIAA / CE international certification and lifespan tracking.',
    legal_btn: 'View Official SERNATUR Record',
    legal_qr_badge: 'SERVICE FILE Nº 90240',
    legal_qr_text: 'Scan to verify registration',
    tech_badge: 'TECHNICAL STANDARDS',
    tech_title: 'Mountain Guides & Field Safety',
    tech_angm_label: 'Mountain Guides',
    tech_angm_desc: 'National Mountain Guides Association (ANGM). Certified technical competencies in rock, glaciers, and alpine rescue.',
    tech_wfr_label: 'Wilderness Medicine',
    tech_wfr_desc: 'Wilderness First Responder (WFR). Advanced first aid and patient stabilization in remote wilderness environments.',
    tech_gps_label: 'Satellite Communication',
    tech_gps_desc: 'Two-way Garmin InReach satellite device with real-time GPS tracking and 24/7 SOS emergency beacon with every group.',
    rescue_badge: 'INSURE YOUR EXPEDITION',
    rescue_desc: 'Patagonian expeditions navigate remote and isolated terrain. Through our partnership with Global Rescue, you can insure your journey with field rescue, aeromedical evacuation, and 24/7 medical advisory from anywhere in the wilderness.',
    rescue_item1: 'Field rescue & aeromedical evacuation',
    rescue_item2: '24/7 Wilderness medical advisory',
    rescue_item3: 'Direct coordination with our field team',
    rescue_btn: 'Insure with Global Rescue',
    rescue_note: 'Official Partner · No deductible',
    plan_title: 'Plan your expedition in Aysén',
    plan_desc: 'Explore our routes in Villa Cerro Castillo or write to us directly on WhatsApp to coordinate your trip.',
    plan_btn: 'Explore Expeditions',
    plan_wa: 'Chat on WhatsApp'
  },
  pt: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Compromisso Sustentável',
    header_subtitle: 'Nossas políticas de sustentabilidade com a SERNATUR, o caminho rumo à emissão zero e as certificações oficiais de montanha em Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CHILE',
    sust_title: 'Turismo Sustentável',
    sust_desc: 'Assinado oficialmente sob as diretrizes de boas práticas da Sernatur.',
    sust_verify: 'Verificar na Sernatur',
    sello_title: 'Nosso caminho rumo ao Selo S',
    sello_desc: 'Na Fisura Expeditions, buscamos fazer da sustentabilidade nosso principal diferencial. Assinamos formalmente o Compromisso de Turismo Sustentável com a SERNATUR e estamos em processo de obter o Selo S, a distinção de sustentabilidade mais rigorosa do Chile, com a meta de conquistá-la durante este ano.',
    zero_badge: 'Médio Prazo · Emissão Zero',
    zero_title: 'Tours com emissão zero de carbono',
    zero_desc: 'Avançamos rumo ao transporte em vans elétricas, contratação exclusiva de guias e tropeiros locais de Villa Cerro Castillo e alimentação em embalagens biodegradáveis com reciclagem total. Ao final de cada tour, entregamos um relatório de sustentabilidade com o CO₂ evitado e os quilos reciclados.',
    regen_badge: 'Longo Prazo · Turismo Regenerativo',
    regen_title: 'Conservação e regeneração ativa',
    regen_desc: 'Integramos em nossas expedições ações práticas com os viajantes, como o plantio de árvores nativas na bacia do Rio Ibáñez e mutirões de limpeza e reciclagem nas trilhas do Parque Nacional Cerro Castillo.',
    legal_badge: 'MARCO LEGAL · LEI Nº 20.423',
    legal_title: 'Registro Nacional SERNATUR',
    legal_desc: 'A Fisura Expeditions está formalmente inscrita no Registro Nacional de Prestadores de Serviços Turísticos do SERNATUR, cumprindo todas as exigências do Estado do Chile para turismo de aventura e guiamento de montanha.',
    legal_item1_label: 'Plano de Gestão de Riscos:',
    legal_item1_text: 'Protocolos de emergência, contingência climática e evacuação médica protocolados junto às autoridades.',
    legal_item2_label: 'Equipamento homologado:',
    legal_item2_text: 'Cordas, cadeirinhas e equipamentos técnicos com certificação internacional UIAA / CE e controle de vida útil.',
    legal_btn: 'Ver Ficha Oficial SERNATUR',
    legal_qr_badge: 'FICHA SERVIÇO Nº 90240',
    legal_qr_text: 'Escaneie para consultar registro',
    tech_badge: 'PADRÕES TÉCNICOS',
    tech_title: 'Guias e Segurança em Campo',
    tech_angm_label: 'Guias de Montanha',
    tech_angm_desc: 'Associação Nacional de Guias de Montanha. Competências técnicas em rocha, geleiras e resgate alpino.',
    tech_wfr_label: 'Medicina de Áreas Remotas',
    tech_wfr_desc: 'Wilderness First Responder. Primeiros socorros avançados e estabilização de pacientes em áreas remotas.',
    tech_gps_label: 'Comunicação Via Satélite',
    tech_gps_desc: 'Dispositivo satelital bidirecional Iridium em cada grupo com rastreamento GPS e botão SOS de emergência.',
    rescue_badge: 'ASSEGURE SUA EXPEDIÇÃO',
    rescue_desc: 'As expedições na Patagônia percorrem terrenos isolados e remotos. Através de nossa parceria com a Global Rescue, você pode proteger sua viagem com resgate de campo, evacuação aeromédica e assessoria de emergência 24 horas.',
    rescue_item1: 'Resgate de campo e evacuação aeromédica',
    rescue_item2: 'Assessoria médica de emergência 24/7',
    rescue_item3: 'Coordenação direta com nossa equipe em campo',
    rescue_btn: 'Proteger com Global Rescue',
    rescue_note: 'Parceiro Oficial · Sem franquia',
    plan_title: 'Planeje sua expedição em Aysén',
    plan_desc: 'Confira nossas rotas em Villa Cerro Castillo ou fale conosco pelo WhatsApp para organizar sua viagem.',
    plan_btn: 'Ver Expedições',
    plan_wa: 'Falar no WhatsApp'
  },
  fr: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Engagement Durable',
    header_subtitle: 'Nos politiques de durabilité avec SERNATUR, la feuille de route vers zéro émission et les certifications officielles de montagne à Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CHILI',
    sust_title: 'Tourisme Durable',
    sust_desc: 'Signé officiellement selon les directives de bonnes pratiques de Sernatur.',
    sust_verify: 'Vérifier sur Sernatur',
    sello_title: 'Notre démarche vers le Label S',
    sello_desc: "Chez Fisura Expeditions, la durabilité est notre priorité absolue. Nous avons formellement signé l'Engagement pour le Tourisme Durable avec SERNATUR et travaillons pour obtenir le Label S, la distinction environnementale la plus rigoureuse du Chili.",
    zero_badge: 'Moyen Terme · Zéro Émission',
    zero_title: 'Tours à zéro émission carbone',
    zero_desc: "Nous avançons vers des vans d'expédition électriques, l'emploi exclusif de guides et muletiers locaux de Villa Cerro Castillo, et des repas dans des emballages 100 % biodégradables. À chaque fin de tour, nous remettons une fiche de durabilité détaillant le CO₂ évité et les déchets recyclés.",
    regen_badge: 'Long Terme · Tourisme Régénératif',
    regen_title: 'Conservation et régénération active',
    regen_desc: "Nous intégrons des actions concrètes avec les voyageurs, comme la plantation d'arbres indigènes dans le bassin du fleuve Ibáñez et des opérations de nettoyage des sentiers du Parc National Cerro Castillo.",
    legal_badge: 'CADRE LÉGAL · LOI Nº 20.423',
    legal_title: 'Registre National SERNATUR',
    legal_desc: "Fisura Expeditions est officiellement enregistrée auprès du Registre National des Prestataires Touristiques (SERNATUR), répondant aux normes de sécurité chiliennes pour le tourisme d'aventure.",
    legal_item1_label: 'Plan de Gestion des Risques :',
    legal_item1_text: "Protocoles d'urgence, météo et évacuation médicale officiellement déposés auprès des autorités.",
    legal_item2_label: 'Équipement certifié :',
    legal_item2_text: 'Cordes, baudriers et matériel technique certifiés UIAA / CE avec contrôle rigoureux de durée de vie.',
    legal_btn: 'Voir la Fiche Officielle SERNATUR',
    legal_qr_badge: 'FICHE SERVICE Nº 90240',
    legal_qr_text: "Scannez pour vérifier l'enregistrement",
    tech_badge: 'NORMES TECHNIQUES',
    tech_title: 'Guides et Sécurité sur le Terrain',
    tech_angm_label: 'Guides de Montagne',
    tech_angm_desc: 'Association Nationale des Guides de Montagne. Compétences en rocher, glaciers et secours alpin.',
    tech_wfr_label: 'Médecine en Milieu Sauvage',
    tech_wfr_desc: 'Wilderness First Responder. Premiers secours avancés et stabilisation de blessés en zones isolées.',
    tech_gps_label: 'Communication Satellite',
    tech_gps_desc: 'Balise satellite bidirectionnelle Iridium dans chaque groupe avec suivi GPS et bouton SOS d’urgence.',
    rescue_badge: 'ASSUREZ VOTRE EXPÉDITION',
    rescue_desc: 'Les expéditions en Patagonie traversent des terrains reculés et sauvages. Grâce à notre partenariat avec Global Rescue, assurez votre aventure avec sauvetage sur le terrain, évacuation aéroportée et assistance médicale 24/7.',
    rescue_item1: 'Sauvetage sur le terrain et évacuation aéroportée',
    rescue_item2: 'Assistance médicale en milieu sauvage 24/7',
    rescue_item3: 'Coordination directe avec notre équipe sur le terrain',
    rescue_btn: "S'assurer avec Global Rescue",
    rescue_note: 'Partenaire Officiel · Sans franchise',
    plan_title: 'Planifiez votre expédition en Aysén',
    plan_desc: 'Découvrez nos itinéraires à Villa Cerro Castillo ou écrivez-nous sur WhatsApp pour préparer votre départ.',
    plan_btn: 'Explorer les Expéditions',
    plan_wa: 'Discuter sur WhatsApp'
  },
  it: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Impegno Sostenibile',
    header_subtitle: 'Le nostre politiche di sostenibilità con SERNATUR, il percorso verso le emissioni zero e le certificazioni ufficiali di montagna a Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CILE',
    sust_title: 'Turismo Sostenibile',
    sust_desc: 'Firmato ufficialmente secondo le linee guida di buone pratiche di Sernatur.',
    sust_verify: 'Verifica su Sernatur',
    sello_title: 'Il nostro percorso verso il Sigillo S',
    sello_desc: "A Fisura Expeditions puntiamo sulla sostenibilità come segno distintivo. Abbiamo firmato formalmente l'Impegno per il Turismo Sostenibile con SERNATUR e stiamo lavorando per ottenere il Sigillo S, il riconoscimento più rigoroso del Cile.",
    zero_badge: 'Medio Termine · Zero Emissioni',
    zero_title: 'Tour a zero emissioni di carbonio',
    zero_desc: 'Avanziamo verso van elettrici, impiego esclusivo di guide e mulattieri locali di Villa Cerro Castillo e pasti in imballaggi biodegradabili con riciclo totale. Al termine del tour, consegniamo un report di sostenibilità con CO₂ evitata e chili riciclati.',
    regen_badge: 'Lungo Termine · Turismo Rigenerativo',
    regen_title: 'Conservazione e rigenerazione attiva',
    regen_desc: 'Integriamo azioni pratiche insieme ai viaggiatori, come la piantumazione di specie autoctone nel bacino del fiume Ibáñez e giornate di pulizia dei sentieri nel Parco Nazionale Cerro Castillo.',
    legal_badge: 'QUADRO GIURIDICO · LEGGE Nº 20.423',
    legal_title: 'Registro Nazionale SERNATUR',
    legal_desc: "Fisura Expeditions è regolarmente iscritta al Registro Nazionale dei Fornitori di Servizi Turistici di SERNATUR, nel pieno rispetto degli standard statali cileni per il turismo d'avventura.",
    legal_item1_label: 'Piano di Gestione dei Rischi:',
    legal_item1_text: 'Protocolli di emergenza, meteo ed evacuazione medica depositati presso le autorità.',
    legal_item2_label: 'Equipaggiamento omologato:',
    legal_item2_text: "Corde, imbragature e materiale tecnico certificati UIAA / CE con controllo rigoroso dell'usura.",
    legal_btn: 'Vedi Scheda Ufficiale SERNATUR',
    legal_qr_badge: 'SCHEDA SERVIZIO Nº 90240',
    legal_qr_text: 'Scansiona per verificare il registro',
    tech_badge: 'STANDARD TECNICI',
    tech_title: 'Guide e Sicurezza sul Campo',
    tech_angm_label: 'Guide Alpine',
    tech_angm_desc: 'Associazione Nazionale Guide Alpine. Competenze tecniche su roccia, ghiacciai e soccorso alpino.',
    tech_wfr_label: 'Medicina in Aree Remote',
    tech_wfr_desc: 'Wilderness First Responder. Primo soccorso avanzato e stabilizzazione pazienti in aree remote.',
    tech_gps_label: 'Comunicazione Satellitare',
    tech_gps_desc: 'Dispositivo satellitare bidirezionale Iridium in ogni gruppo con tracciamento GPS e pulsante SOS.',
    rescue_badge: 'ASSICURA LA TUA SPEDIZIONE',
    rescue_desc: 'Le spedizioni in Patagonia attraversano terreni impervi e isolati. Grazie alla nostra partnership con Global Rescue, puoi assicurare il viaggio con soccorso sul campo, evacuazione aerea e assistenza medica 24/7.',
    rescue_item1: 'Soccorso sul campo ed evacuazione aerea',
    rescue_item2: "Assistenza medica d'emergenza 24/7",
    rescue_item3: 'Coordinamento diretto con il nostro team sul campo',
    rescue_btn: 'Assicurati con Global Rescue',
    rescue_note: 'Partner Ufficiale · Senza franchigia',
    plan_title: 'Pianifica la tua spedizione in Aysén',
    plan_desc: 'Scopri i nostri percorsi a Villa Cerro Castillo o contattaci su WhatsApp per organizzare la partenza.',
    plan_btn: 'Esplora le Spedizioni',
    plan_wa: 'Scrivici su WhatsApp'
  },
  de: {
    header_badge: 'Fisura Expeditions · Aysén',
    header_title: 'Nachhaltiges Engagement',
    header_subtitle: 'Unsere Nachhaltigkeitspolitik mit SERNATUR, der Weg zu null Emissionen und offizielle Bergzertifizierungen in Villa Cerro Castillo.',
    sust_badge: 'SERNATUR CHILE',
    sust_title: 'Nachhaltiger Tourismus',
    sust_desc: 'Offiziell unterzeichnet nach den Richtlinien bewährter Praktiken von Sernatur.',
    sust_verify: 'Bei Sernatur prüfen',
    sello_title: 'Unser Weg zum S-Siegel',
    sello_desc: 'Bei Fisura Expeditions ist Nachhaltigkeit unser zentrales Anliegen. Wir haben das Abkommen für nachhaltigen Tourismus mit SERNATUR unterzeichnet und arbeiten auf das S-Siegel hin, Chiles anspruchsvollste Nachhaltigkeitsauszeichnung.',
    zero_badge: 'Mittelfristig · Null Emissionen',
    zero_title: 'Touren mit null CO₂-Emissionen',
    zero_desc: 'Wir setzen auf elektrische Vans, die ausschließliche Beschäftigung lokaler Bergführer und Pferdeführer aus Villa Cerro Castillo sowie biologisch abbaubare Verpackungen. Nach jeder Tour erhalten Gäste einen Nachhaltigkeitsbericht über eingespartes CO₂ und recycelte Abfälle.',
    regen_badge: 'Langfristig · Regenerativer Tourismus',
    regen_title: 'Aktiver Schutz und Regeneration',
    regen_desc: 'Wir binden Reisende aktiv ein, etwa durch das Pflanzen einheimischer Bäume im Flusstal des Río Ibáñez und Müllsammelaktionen im Cerro Castillo Nationalpark.',
    legal_badge: 'GESETZLICHER RAHMEN · GESETZ NR. 20.423',
    legal_title: 'Nationales SERNATUR-Register',
    legal_desc: 'Fisura Expeditions ist offiziell im nationalen Register für Tourismusdienstleister (SERNATUR) eingetragen und erfüllt alle staatlichen Sicherheitsstandards für Abenteuertourismus.',
    legal_item1_label: 'Risikomanagementplan:',
    legal_item1_text: 'Notfall-, Wetter- und medizinische Evakuierungsprotokolle offiziell bei den Behörden hinterlegt.',
    legal_item2_label: 'Zertifizierte Ausrüstung:',
    legal_item2_text: 'Seile, Klettergurte und technische Bergausrüstung mit UIAA / CE-Zertifizierung und Lebensdauerprüfung.',
    legal_btn: 'Offiziellen SERNATUR-Eintrag ansehen',
    legal_qr_badge: 'DIENSTE-EINTRAG NR. 90240',
    legal_qr_text: 'Scannen zur Registrierungsprüfung',
    tech_badge: 'TECHNISCHE STANDARDS',
    tech_title: 'Guides und Sicherheit vor Ort',
    tech_angm_label: 'Bergführer',
    tech_angm_desc: 'Nationaler Bergführerverband. Technische Kompetenzen in Fels, Gletschern und alpiner Rettung.',
    tech_wfr_label: 'Wildnismedizin',
    tech_wfr_desc: 'Wilderness First Responder. Erweiterte Erste Hilfe und Patientenstabilisierung in abgelegenen Gebieten.',
    tech_gps_label: 'Satellitenkommunikation',
    tech_gps_desc: 'Zwei-Wege-Iridium-Satellitengerät in jeder Gruppe mit GPS-Ortung und SOS-Notfalltaste.',
    rescue_badge: 'SICHERE DEINE EXPEDITION AB',
    rescue_desc: 'Patagonische Expeditionen führen durch abgelegenes Gelände. Durch unsere Partnerschaft mit Global Rescue kannst du deine Reise mit Feldrettung, medizinischer Evakuierung und 24/7-Notfallberatung absichern.',
    rescue_item1: 'Feldrettung und medizinische Notevakuierung',
    rescue_item2: '24/7 medizinische Wildnisberatung',
    rescue_item3: 'Direkte Abstimmung mit unserem Team vor Ort',
    rescue_btn: 'Mit Global Rescue absichern',
    rescue_note: 'Offizieller Partner · Ohne Selbstbeteiligung',
    plan_title: 'Plane deine Expedition in Aysén',
    plan_desc: 'Entdecke unsere Routen in Villa Cerro Castillo oder schreibe uns direkt auf WhatsApp.',
    plan_btn: 'Expeditionen ansehen',
    plan_wa: 'Auf WhatsApp chatten'
  }
};

export const Certifications: React.FC<CertificationsProps> = ({ t: _t, lang = 'es' }) => {
  const i18n = CERT_I18N[lang] || CERT_I18N.es;
  const sernaturUrl = "https://registro.sernatur.cl/registroempresarios/fichaservicio?sid=90240&sign=3b195eafd6db271279ff0f03457301f0";
  const sustentabilidadUrl = "https://sustentabilidad.sernatur.cl/";

  return (
    <div className="bg-[#0a0a0a] text-[#f2f2f2] min-h-screen selection:bg-[#00d4ff] selection:text-black">
      
      {/* 1. CABECERA SIMPLE */}
      <section className="pt-28 md:pt-36 pb-8 md:pb-10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 space-y-2">
          <span className="text-xs font-mono tracking-[0.25em] text-[#00d4ff] uppercase block">
            {i18n.header_badge}
          </span>
          <h1 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
            {i18n.header_title}
          </h1>
          <p className="text-sm sm:text-base text-gray-400 font-light max-w-2xl leading-relaxed">
            {i18n.header_subtitle}
          </p>
        </div>
      </section>

      {/* 2. TURISMO SUSTENTABLE Y RUTA AL SELLO S */}
      <section className="py-12 md:py-16 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
            
            {/* Sello Oficial */}
            <div className="md:col-span-4 flex flex-col items-center text-center">
              <div className="w-36 h-36 sm:w-44 sm:h-44 mb-3">
                <img
                  src="/logo-compromiso-sustentable.png"
                  alt="Compromiso Turismo Sustentable SERNATUR"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xs uppercase tracking-widest text-emerald-400 font-bold block">
                {i18n.sust_badge}
              </span>
              <h2 className="font-title text-base text-white uppercase tracking-wider mt-1">
                {i18n.sust_title}
              </h2>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                {i18n.sust_desc}
              </p>
              <a
                href={sustentabilidadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 text-xs text-emerald-400 hover:text-white transition-colors font-mono tracking-wider inline-flex items-center gap-1.5"
              >
                <span>{i18n.sust_verify}</span>
                <ExternalLink size={13} />
              </a>
            </div>

            {/* Texto y Visión */}
            <div className="md:col-span-8 space-y-6">
              <div className="space-y-3">
                <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white">
                  {i18n.sello_title}
                </h3>
                <p className="text-sm sm:text-base text-gray-300 font-light leading-relaxed">
                  {i18n.sello_desc}
                </p>
              </div>

              {/* Objetivos mediano y largo plazo */}
              <div className="space-y-6 pt-2">
                
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-emerald-400 font-bold block">
                    {i18n.zero_badge}
                  </span>
                  <h4 className="font-title text-base uppercase tracking-wider text-white">
                    {i18n.zero_title}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-400 font-light leading-relaxed">
                    {i18n.zero_desc}
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-[#00d4ff] font-bold block">
                    {i18n.regen_badge}
                  </span>
                  <h4 className="font-title text-base uppercase tracking-wider text-white">
                    {i18n.regen_title}
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-400 font-light leading-relaxed">
                    {i18n.regen_desc}
                  </p>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 3. REGISTRO OFICIAL SERNATUR */}
      <section className="py-12 md:py-16 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 space-y-8">
          
          <div className="border-b border-white/10 pb-4">
            <span className="text-xs font-mono uppercase tracking-wider text-[#00d4ff] block mb-1">
              {i18n.legal_badge}
            </span>
            <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white">
              {i18n.legal_title}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            <div className="md:col-span-8 space-y-4">
              <p className="text-sm sm:text-base text-gray-300 font-light leading-relaxed">
                {i18n.legal_desc}
              </p>

              <div className="space-y-3 pt-1 text-xs sm:text-sm text-gray-400 font-light">
                <div className="flex items-start gap-2">
                  <span className="text-[#00d4ff] font-bold">—</span>
                  <span><strong>{i18n.legal_item1_label}</strong> {i18n.legal_item1_text}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#00d4ff] font-bold">—</span>
                  <span><strong>{i18n.legal_item2_label}</strong> {i18n.legal_item2_text}</span>
                </div>
              </div>

              <div className="pt-2">
                <a
                  href={sernaturUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black font-title text-xs uppercase tracking-widest hover:bg-[#00d4ff] transition-all"
                >
                  <span>{i18n.legal_btn}</span>
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>

            {/* QR y número de ficha */}
            <div className="md:col-span-4 flex flex-col items-center text-center border border-white/15 p-6">
              <img
                src="/sernatur/logo-sernatur-sin-fecha-transparent.png"
                alt="SERNATUR"
                className="h-14 w-auto object-contain mb-3"
              />
              <div className="p-2.5 bg-white mb-3">
                <QRCodeSVG
                  value={sernaturUrl}
                  size={110}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#0a0a0a"
                />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-[#00d4ff] font-bold block">
                {i18n.legal_qr_badge}
              </span>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">
                {i18n.legal_qr_text}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 4. GUÍAS Y SEGURIDAD */}
      <section className="py-12 md:py-16 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 space-y-6">
          
          <div className="border-b border-white/10 pb-3">
            <span className="text-xs font-mono uppercase tracking-wider text-gray-400 block mb-1">
              {i18n.tech_badge}
            </span>
            <h3 className="text-xl sm:text-2xl font-title uppercase tracking-tight text-white">
              {i18n.tech_title}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            
            <div className="border border-white/10 p-5 space-y-2">
              <span className="font-mono text-xs text-[#00d4ff] uppercase block">{i18n.tech_angm_label}</span>
              <h4 className="font-title text-xl uppercase tracking-wider text-white">ANGM</h4>
              <p className="text-xs sm:text-sm text-gray-400 font-light leading-relaxed">
                {i18n.tech_angm_desc}
              </p>
            </div>

            <div className="border border-white/10 p-5 space-y-2">
              <span className="font-mono text-xs text-red-400 uppercase block">{i18n.tech_wfr_label}</span>
              <h4 className="font-title text-xl uppercase tracking-wider text-white">WFR</h4>
              <p className="text-xs sm:text-sm text-gray-400 font-light leading-relaxed">
                {i18n.tech_wfr_desc}
              </p>
            </div>

            <div className="border border-white/10 p-5 space-y-2">
              <span className="font-mono text-xs text-amber-400 uppercase block">{i18n.tech_gps_label}</span>
              <h4 className="font-title text-xl uppercase tracking-wider text-white">Garmin InReach</h4>
              <p className="text-xs sm:text-sm text-gray-400 font-light leading-relaxed">
                {i18n.tech_gps_desc}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* 5. SEGURO DE EXPEDICIÓN - GLOBAL RESCUE */}
      <section className="py-12 md:py-16 border-b border-white/10 bg-white/[0.015]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="border border-white/15 p-6 sm:p-10 bg-[#0e0e0e] relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-4">
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#00d4ff] font-bold block">
                  {i18n.rescue_badge}
                </span>
                
                <div>
                  <img
                    src="/global-rescue-white-red.png"
                    alt="Global Rescue"
                    className="h-8 sm:h-10 w-auto object-contain"
                  />
                </div>

                <p className="text-xs sm:text-sm text-gray-300 font-light leading-relaxed max-w-2xl">
                  {i18n.rescue_desc}
                </p>

                <div className="pt-2 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-400 font-light">
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 font-bold">—</span>
                    <span>{i18n.rescue_item1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 font-bold">—</span>
                    <span>{i18n.rescue_item2}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 font-bold">—</span>
                    <span>{i18n.rescue_item3}</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col items-start lg:items-end justify-center gap-2">
                <a
                  href="https://www.globalrescue.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    if (typeof (window as any).gtag === 'function') {
                      (window as any).gtag('event', 'click_global_rescue', {
                        location: 'certifications_page'
                      });
                    }
                  }}
                  className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-title text-xs uppercase tracking-widest transition-all inline-flex items-center gap-2 shadow-lg shadow-red-950/40"
                >
                  <span>{i18n.rescue_btn}</span>
                  <ExternalLink size={14} />
                </a>
                <span className="text-[10px] text-gray-500 font-mono tracking-wider">
                  {i18n.rescue_note}
                </span>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 6. CIERRE */}
      <section className="py-14 text-center">
        <div className="max-w-2xl mx-auto px-6 space-y-4">
          <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white">
            {i18n.plan_title}
          </h3>
          <p className="text-sm text-gray-400 font-light leading-relaxed">
            {i18n.plan_desc}
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={getLocalizedPath('/expeditions', lang)}
              className="px-6 py-2.5 bg-white text-black font-title text-xs uppercase tracking-widest hover:bg-[#00d4ff] transition-all inline-flex items-center gap-2"
            >
              <span>{i18n.plan_btn}</span>
              <ArrowRight size={14} />
            </Link>

            <a
              href="https://wa.me/56967273815"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 border border-white/30 text-white font-title text-xs uppercase tracking-widest hover:border-white hover:bg-white/10 transition-all"
            >
              {i18n.plan_wa}
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Certifications;
